import { Component, ViewChild, ViewEncapsulation, OnInit, HostBinding } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../../models/base-modal-component';
import { Endpoint, UpdateStatusType, HelpIndex, LoadFileType } from '../../../../models/enums';
import { GenericColDef, GridRowSelectionType, SortByFilter, AlignDirection } from '../../../../models/grid-context';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { ConnectorPickerComponent } from '../../../connector-picker/connector-picker.component';
import * as _ from 'underscore';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { PreviewField } from '../../../../models/preview-field';
import { Selectable } from '../../../../models/selectable';

@Component({
    standalone: false,
    selector: 'res-load-file-discover-fields-modal',
    templateUrl: './load-file-import-discover-fields-modal.component.html',
    styleUrls: ['./load-file-import-discover-fields-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LoadFileDiscoverFieldsModalComponent extends BaseModalComponent implements OnInit {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGridPreview: GenericGridComponent;
    @ViewChild(ConnectorPickerComponent, { static: true }) connectorPicker: ConnectorPickerComponent;

    modalTitle = 'Discover Fields';
    modalClass = 'load-file-import-discover-fields-modal';
    helpIndex = HelpIndex.load_file_discover_fields_tab;
    previewRequestInfo: DrRequestInfo;
    previewGridName = 'Preview';
    rowSelection = GridRowSelectionType.single;
    connectorUpdateStatusType = UpdateStatusType.CONNECTOR;
    isBusy = false;
    isPreviewBusy = false;
    isBusyMessage = 'Loading ...';
    isBusyPreviewMessage = '';
    primaryButtonLabel = 'OK';
    secondaryButtonLabel = 'Cancel';
    modalAutomationId = 'load-file-import-discover-fields';
    path = '';
    encodingTypes = [
        { name: 'ISO8859', value: 'iso8859' },
        { name: 'UTF8', value: 'utf8' },
        { name: 'UTF16 LE', value: 'utf16le' },
        { name: 'UTF16 BE', value: 'utf16be' },
        { name: 'MIXED MODE', value: 'mixedmode' }];
    selectedEncoding: any;
    selectedSpecialChars: any;
    specialCharacters: Array<{ name: string, value: number }>;
    delimiters = [
        { name: 'Column', field: 'FieldDelimiter' },
        { name: 'Quote', field: 'Quote' },
        { name: 'Multi Value', field: 'MultiValue' },
        { name: 'Nested Value', field: 'NestedValue' },
        { name: 'New Line', field: 'NewLine' }];
    connector: any;
    profile: any;
    fields: Array<any>;

    connectorColumnDefs = new Array<GenericColDef>(
        {
            headerName: 'Connector Name',
            field: 'name',
            automationId: this.modalAutomationId,
            sortBy: SortByFilter.NAME.toString(),
            lockVisible: true
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.modalAutomationId
        },
        {
            headerName: 'Type',
            field: 'type',
            automationId: this.modalAutomationId,
            width: 110,
            suppressSizeToFit: true
        },
        {
            headerName: 'Mode',
            field: 'modeDisplay',
            automationId: this.modalAutomationId,
            width: 95,
            suppressSizeToFit: true
        },
        {
            headerName: 'Server',
            field: 'remoteHost',
            automationId: this.modalAutomationId
        },
        {
            headerName: 'Path',
            field: 'remotePath',
            automationId: this.modalAutomationId
        },
    );

    previewColumnDefs = new Array<GenericColDef>(
        {
            headerName: 'Column',
            field: 'name',
            automationId: this.modalAutomationId,
            lockVisible: true,
            headerAlignDirection: AlignDirection.left,
            cellAlignDirection: AlignDirection.left,
        },
        {
            headerName: 'Value',
            field: 'value',
            automationId: this.modalAutomationId,
            headerAlignDirection: AlignDirection.left,
            cellAlignDirection: AlignDirection.left,
        },
    );

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.loadFileMetadataProfileData();
        if (this.modalData) {
            this.selectedEncoding = this.modalData.encodingType ? this.encodingTypes[_.indexOf(_.pluck(this.encodingTypes, 'value'), this.modalData.encodingType)] : this.encodingTypes[1];
            this.selectedSpecialChars = { FieldDelimiter: '', Quote: '', MultiValue: '', NestedValue: '', NewLine: '' };
            this.modalData.loadFileType = this.modalData.loadFileType ? this.modalData.loadFileType : LoadFileType.CONCORDANCE_DAT;
            this.modalData.encodingType = this.modalData.encodingType ? this.modalData.encodingType : this.selectedEncoding.value;
            this.modalData.loadMode = this.modalData.loadMode ? this.modalData.loadMode : 'REPLACE';
        }
    }

    loadFileMetadataProfileData(): void {
        this.restService.post(Endpoint.projectManager_getLoadFileMetadataPrepareData).then(response => {
            if (response) {
                this.specialCharacters = response.availableSpecialCharacters;
            }
            if (this.modalData && this.modalData.specialCharacters && this.specialCharacters) {
                this.modalData.specialCharacters.forEach((char) => {
                    this.selectedSpecialChars[char.name] = this.specialCharacters[this.getIndex(char.value, 'value')];
                });
            }
        });
    }

    filter(chars: any): any {
        if (chars) {
            chars.forEach((char, index) => {
                char.name = (index >= 0 && index <= 31) || (index >= 127 && index <= 159) ? char.name : String.fromCharCode(char.value);
            });
        }
        return chars;
    }

    previewResponseArrayCallback = (value: any) => new PreviewField(value);

    previewResponseCallback = (fields: any) => {
        if (fields) {
            this.isBusy = false;
            this.fields = fields.loadFileFields.map(field => {
                return new Selectable({ externalName: field.name });
            });
        }
    }

    isPrimaryButtonEnabled(): boolean {
        return !this.isBusy && !!this.path;
    }

    ok(): void {
        this.updatePreview(true);
        this.closeModal(true, { fields: this.fields, profile: this.modalData, isXml: this.hideSettingsPanel() });
    }

    getPath(path) {
        this.path = path;
        if (this.path) {
            if (this.path.endsWith('.csv')) {
                this.setDelimiterDefaults('csv');
            }
            if (this.path.endsWith('.dat')) {
                this.setDelimiterDefaults('dat');
            }
            this.loadPreviewData();
        } else {
            this.genericGridPreview.setData([]);
        }
    }

    updatePreview(fromOk = false): void {
        if (this.modalData) {
            this.isBusy = true;
            this.isBusyMessage = 'Updating load file settings ...';

            const profile = {
                name: this.modalData.name,
                description: this.modalData.description ? this.modalData.description : '',
                profileHandle: this.modalData.id,
                defaultProfile: this.modalData.defaultProfile,
                encodingType: this.selectedEncoding ? this.selectedEncoding.value : null,
                loadFileType: this.path.endsWith('.xml') ? LoadFileType.EDRM_XML : (this.modalData ? this.modalData.loadFileType : null),
                loadImages: this.modalData ? this.modalData.loadImages : false,
                loadMode: this.modalData ? this.modalData.loadMode : null,
                preserveFamily: this.modalData ? this.modalData.preserveFamily : false,
                specialCharacters: this.mapSpecialCharacters(),
                version: this.modalData ? this.modalData.version : null,
                entries: this.modalData ? this.modalData.entries : [],
                availableLoadFileFields: this.modalData ? this.modalData.availableLoadFileFields : []
            };
            this.restService.post(Endpoint.projectManager_setLoadFileMetadataProfile, profile).then(response => {
                this.modalData.specialCharacters = response.profile.specialCharacters;
                this.modalData.encodingType = response.profile.encodingType;
                this.isBusy = false;
                if (this.path && !fromOk) {
                    this.loadPreviewData();
                }
            }, () => {
                this.isBusy = false;
            });
        }
    }

    mapSpecialCharacters(): any {
        const chars = [];
        if (this.selectedSpecialChars) {
            for (const [key, value] of Object.entries(this.selectedSpecialChars)) {
                chars.push({ name: key, value: (value as any).value });
            }
        }
        return chars;
    }

    loadPreviewData(): void {
        this.connector = {
            handle: this.connectorPicker.selectedConnector.handle,
            mode: this.connectorPicker.selectedConnector.mode,
            name: this.connectorPicker.selectedConnector.name,
            networkId: this.connectorPicker.selectedConnector.networkId,
            offset: this.connectorPicker.selectedConnector.offset,
            status: this.connectorPicker.selectedConnector.status,
            type: this.connectorPicker.selectedConnector.type
        };

        const dataArea = {
            connectorHandle: this.connectorPicker.selectedConnector.handle,
            name: this.connectorPicker.currentSelectedPath,
            mode: 'IMPORT',
            path: this.connectorPicker.currentSelectedPath
        };

        this.previewRequestInfo = new DrRequestInfo({
            endpoint: Endpoint.projectManager_discoverLoadFile,
            responseName: 'loadFileFields',
            data: {
                relativePath: this.connectorPicker.selectedFile.name,
                dataAreaPath: this.connectorPicker.currentSelectedPath,
                loadFileMetadataProfileDto: this.modalData,
                connector: this.connector
            }
        });

        this.genericGridPreview.requestInfo = this.previewRequestInfo;
        this.genericGridPreview.loadData();
    }

    setDelimiterDefaults(type): void {
        if (this.selectedSpecialChars && this.specialCharacters) {
            this.selectedSpecialChars.FieldDelimiter = this.specialCharacters[this.getIndex(type === 'csv' ? ',' : 'ASCII-020', 'name')];
            this.selectedSpecialChars.Quote = this.specialCharacters[this.getIndex(type === 'csv' ? '"' : 'þ', 'name')];
            this.selectedSpecialChars.MultiValue = this.specialCharacters[this.getIndex(';', 'name')];
            this.selectedSpecialChars.NestedValue = this.specialCharacters[this.getIndex('\\', 'name')];
            this.selectedSpecialChars.NewLine = this.specialCharacters[this.getIndex('®', 'name')];
        }
    }

    hideSettingsPanel(): boolean {
        return !this.path || this.path?.endsWith('.xml');
    }

    getIndex(delimiter, type): number {
        const index = _.indexOf(_.pluck(this.specialCharacters, type), delimiter);
        return index;
    }
}
